/**
 * Il Reference Id di un prodotto del catalogo ZDHC: che forma ha, e quando vale.
 *
 * Stava scritto in due posti che non si parlavano — la validazione dell'input
 * manuale in `ProductAssociationSection` e la normalizzazione in `matchStatus`,
 * che il formato non lo guardava affatto. Il formato e' la stessa conoscenza per
 * entrambi, e due copie di una regola divergono appena una viene corretta.
 */

/**
 * La forma che il backend impone (`ZdhcGatewayService::PRODUCT_REFERENCE_PATTERN`).
 *
 * Non e' una regola nostra piu' severa della sua: l'api la applica sia alla
 * lettura del dettaglio (`Zdhc/ProductCatalogueController@show` risponde 422)
 * sia al payload dell'inventario (`Zdhc/InventoryController`). Un id che non la
 * rispetta non e' spendibile da **nessuna** delle due porte, quindi trattarlo
 * come assente non toglie niente a nessuno.
 */
export const REFERENCE_ID_PATTERN = /^20-[A-Z0-9]{8,16}-[A-Z0-9]$/i;

/**
 * Il Reference Id se c'e' qualcosa di scritto, altrimenti `null`. Il formato
 * non lo guarda.
 *
 * Due cose valgono come assenza:
 *
 * 1. un tipo diverso da stringa — il contratto e' `string|null`, e un `{}`
 *    arrivato per sbaglio finirebbe in un URL come `[object Object]`;
 * 2. una stringa di soli spazi, che e' **truthy** e quindi senza normalizzarla
 *    sopprime i fallback e parte verso il catalogo per farsi rifiutare.
 *
 * Un id torna trimmato: gli spazi attorno fanno fallire la lookup, e niente li
 * vieta a monte.
 */
export const presentReferenceId = (value) =>
{
    if(typeof value !== 'string') return null;

    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
};

/**
 * Il Reference Id se e' **spendibile**, altrimenti `null`: presente, e con la
 * forma che il Gateway accetta.
 *
 * Distinto da `presentReferenceId` di proposito, e non e' una sfumatura. Chi
 * deve *spendere* l'id — export, schede, payload dell'inventario — con una
 * forma sbagliata non ci fa niente, quindi per lui vale come assente. Chi deve
 * decidere se **rimettere in discussione** lo stato salvato ha bisogno di sapere
 * che qualcosa c'e' scritto: oggi l'api non sa riparare un id malformato
 * (`ZdhcGatewayService::getProduct()` lancia `InvalidArgumentException` prima
 * della richiesta, e `ProductMatchResolver::verifyExistingMatch()` non la
 * cattura), quindi mandarlo a rivalutare produrrebbe un errore a ogni giro
 * invece di una riparazione. Le due funzioni si riuniscono quando l'api sa
 * sciogliere quelle associazioni.
 */
export const usableReferenceId = (value) =>
{
    const present = presentReferenceId(value);

    return present !== null && REFERENCE_ID_PATTERN.test(present) ? present : null;
};


/**
 * Il Reference Id nella forma con cui si **manda** all'api: spendibile, e
 * maiuscolo. Altrimenti `null`.
 *
 * L'ordine dei due passi non e' un dettaglio, ed e' il motivo per cui la
 * maiuscola sta **qui dentro** e non nel chiamante: `toUpperCase()` non lavora
 * in ASCII. `'\u00DF'` diventa `'SS'`, `'\u0131'` diventa `'I'`, `'\u017F'`
 * diventa `'S'` — quindi convertendo prima di validare, un id che il formato
 * rifiuta puo' diventarne uno **valido e diverso**: `20-AAAAAAA\u00DF-A` si
 * trasforma in `20-AAAAAAASS-A`, che passa, e il form spedirebbe un'identita'
 * che nessuno ha mai scritto. Validando prima, resta `null` e chi chiama sa
 * che c'e' da correggere.
 *
 * Su un valore gia' spendibile la conversione e' invece innocua: l'alfabeto del
 * formato e' `[A-Z0-9]`, dove `toUpperCase()` e `strtoupper()` decidono allo
 * stesso modo.
 *
 * Distinto dagli altri due di proposito. `presentReferenceId` resta l'eco
 * fedele di cio' che una persona ha scritto — serve a mostrarle la sua cella nel
 * modale di import — e `usableReferenceId` resta la sola domanda sul formato,
 * che alimenta anche le celle della dashboard e il payload dell'inventario, dove
 * il valore arriva gia' canonico dal Gateway e non va rimaneggiato.
 *
 * Perche' convertire, se l'api normalizza comunque in colonna
 * (`ProductReferenceIdNormalizer`): il form e il modale mostrano cio' che si e'
 * appena digitato, e senza, fino al primo ricarico, il campo direbbe un valore
 * diverso da quello che il prodotto porta davvero.
 */
export const canonicalReferenceId = (value) =>
{
    const usable = usableReferenceId(value);

    return usable === null ? null : usable.toUpperCase();
};

/**
 * L'identita' ZDHC persistita sul prodotto locale.
 *
 * Dashboard ed export devono leggere la stessa colonna e applicare la stessa
 * validazione: diversamente la riga puo' mostrare un ID che il CSV perde, oppure
 * l'export puo' interrogare il Gateway con un valore non spendibile.
 */
export const productIdentityReferenceId = (product) =>
    usableReferenceId(product?.product_reference_id);
